import React, { useEffect, useRef, useContext, Fragment, useMemo } from "react";
import { Formik, Form } from "formik";
import FastTextInput from "components/FormElements/FastTextInput";
import Label from "components/FormElements/Label";
import WorkflowField from "./Fields/WorkflowField";
import UuidField from "./Fields/UuidField";
import UserField from "./Fields/UserField";
import StaticDateField from "./Fields/StaticDateField";
import { AssignmentContext } from "context/Assignment";
import RichEditor from "components/FormElements/RichEditorInternal";
import { useRegisterActions, Priority } from "kbar";
import { useAuth } from "hooks/Auth";
// import PromptIfDirty from "components/FormElements/PromptIfDirty";
import ContentList from "./Fields/ContentList";
import DateTimeInput from "components/FormElements/DateTimeInput";
import DateInput from "components/FormElements/DateInput";
import IssueField from "./Fields/IssueField";
import TypeField from "./Fields/TypeField";
import CheckBox from "components/FormElements/CheckBox";
import AssignmentToolbar from "./AssignmentToolbar";
import { toast } from "react-hot-toast";
import AlertBox from "components/General/AlertBox";

function EditForm(props) {
    const formRef = useRef();
    const auth = useAuth();

    const { assignment, loading, formValues, isUserEditable } =
        useContext(AssignmentContext);

    const requestClipboardWrite = (data) => {
        navigator.clipboard.writeText(data).then(() => {
            toast.success("Copied to clipboard");
        });
    };

    const kbarActions = useMemo(() => {
        if (assignment.lock && assignment.lock.user.uuid === auth.user.uuid) {
            return [
                {
                    id: "assignmentSave",
                    name: "Save",
                    shortcut: ["Meta+Enter"],
                    keywords: "save",
                    perform: () => {
                        setTimeout(() => {
                            formRef.current.submitForm();
                        }, 250);
                    },
                    priority: Priority.HIGH,
                },
            ];
        } else {
            return [];
        }
    }, [assignment, auth.user]);

    useRegisterActions(kbarActions, [kbarActions]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: formValues,
            });
        });
    }, [loading, formValues]);

    return (
        <Formik
            innerRef={formRef}
            initialValues={formValues}
            onSubmit={props.onSubmit}
            enableReinitialize={false}
        >
            {({ setFieldValue }) => (
                <Form>
                    {/* < PromptIfDirty />*/}
                    <div className="relative">
                        <div className="sticky top-0 z-10">
                            <AssignmentToolbar />
                        </div>
                        <div className="grid grid-cols-12 gap-4 px-2 sm:px-0">
                            <div className="order-last col-span-12 space-y-4 md:col-span-3">
                                <UuidField />
                                <div className="space-y-4 rounded bg-white p-4 shadow-sm">
                                    <StaticDateField
                                        label="Created At:"
                                        field="created_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Modified At:"
                                        field="modified_at"
                                        defaultValue="Unsaved"
                                    />
                                    <div className="text-sm">
                                        <Label>Created By</Label>
                                        <a
                                            href={"mailto:".concat(
                                                assignment.creator.email
                                            )}
                                            className="text-green-600 underline"
                                        >
                                            {assignment.creator.name}
                                        </a>
                                    </div>
                                    <hr />
                                    <DateInput
                                        label="Due"
                                        name="due_at"
                                        disabled={!isUserEditable}
                                    />
                                </div>
                                <div className="space-y-5 rounded bg-white p-4 shadow-sm">
                                    <WorkflowField />

                                    <CheckBox
                                        label="Completed"
                                        name="completed"
                                        helpertext="Mark this assignment as completed."
                                        disabled={!isUserEditable}
                                    />
                                </div>
                                <div className="space-y-5 rounded bg-white p-4 shadow-sm">
                                    <UserField />
                                    <IssueField />
                                    <TypeField />
                                </div>
                            </div>
                            <div className="order-first col-span-12 space-y-4 md:col-span-9">
                                {loading ? (
                                    <Fragment>
                                        <div>
                                            <Label>Title</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <Label>Description</Label>
                                            <div className="mt-1 h-40 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <div className="mt-1 h-40 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        {assignment.lock &&
                                            assignment.lock.user &&
                                            assignment.lock.user.uuid !==
                                                auth.user.uuid && (
                                                <AlertBox variant="danger">
                                                    This assignment is currently
                                                    checked out to{" "}
                                                    {assignment.lock.user.name}
                                                </AlertBox>
                                            )}

                                        <FastTextInput
                                            label="Title"
                                            name="title"
                                        />

                                        <div>
                                            <Label>Description</Label>
                                            <RichEditor
                                                name="content"
                                                disabled={!isUserEditable}
                                                helpertext="Use '@' to mention a user."
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <FastTextInput
                                                    label="Contact Name"
                                                    name="contact_name"
                                                    disabled={!isUserEditable}
                                                />
                                            </div>
                                            <div className="flex items-end">
                                                <div className="flex-grow">
                                                    <FastTextInput
                                                        label="Contact Phone/Email"
                                                        name="contact_phone"
                                                        disabled={
                                                            !isUserEditable
                                                        }
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        requestClipboardWrite(
                                                            assignment.contact_phone
                                                                ? assignment.contact_phone
                                                                : null
                                                        );
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="m-2 h-6 w-6"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="col-span-2">
                                                <div className="flex items-end">
                                                    <div className="flex-grow">
                                                        <FastTextInput
                                                            label="Location"
                                                            name="location"
                                                            disabled={
                                                                !isUserEditable
                                                            }
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            requestClipboardWrite(
                                                                assignment.location
                                                                    ? assignment.location
                                                                    : null
                                                            );
                                                        }}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="m-2 h-6 w-6"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <DateTimeInput
                                                    label="Starts"
                                                    name="action_start_time"
                                                    disabled={!isUserEditable}
                                                />
                                            </div>
                                            <div>
                                                <DateTimeInput
                                                    label="Ends"
                                                    name="action_end_time"
                                                    disabled={!isUserEditable}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <ContentList />
                                        </div>
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="relative z-20 mt-4">
                        {loading ? (
                            <div className="absolute top-0 left-0 z-50 h-full w-full bg-gray-50">
                                <div className="h-full w-full animate-pulse rounded bg-gray-300"></div>
                            </div>
                        ) : null}
                        <div className="space-y-4"></div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default EditForm;
